import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const EditProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    bio: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoadingProfile(false);
        return;
      }
      
      // Set cookie as fallback (cookies are more reliable than headers on some hosts)
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/talent-profiles?id=${id}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token  // Fallback header
          },
          withCredentials: true  // Important for cookies
        });
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          response = await axios.get(`${API_BASE_URL}/talent-profiles?id=${id}`, {
            params: { token: token },
            headers: { 
              'X-Auth-Token': token  // Still send custom header
            },
            withCredentials: true
          });
        } else {
          throw err;
        }
      }
      
      const profileData = { ...response.data };
      
      // Format dateOfBirth to YYYY-MM-DD format for date input
      if (profileData.dateOfBirth) {
        const date = new Date(profileData.dateOfBirth);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          profileData.dateOfBirth = `${year}-${month}-${day}`;
        }
      }
      
      setFormData(profileData);
      setError('');
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Profile not found. It may have been deleted.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch profile. Please try again.');
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Set cookie as fallback
      document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
      
      // Try with Authorization header first, plus X-Auth-Token as fallback
      try {
        await axios.put(`${API_BASE_URL}/talent-profiles?id=${id}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Auth-Token': token,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      } catch (err) {
        // If 401, try with query parameter as last resort
        if (err.response?.status === 401) {
          console.log('Authorization header failed, trying with query parameter...');
          await axios.put(`${API_BASE_URL}/talent-profiles?id=${id}&token=${token}`, formData, {
            headers: { 
              'X-Auth-Token': token,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          });
        } else {
          throw err;
        }
      }
      
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to update profile:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="form-container">
      <h2>Edit Profile</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth:</label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            value={formData.dateOfBirth || ''}
            onChange={handleChange}
            min="1900-01-01"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">Address:</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">City:</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="state">State:</label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="zipCode">ZIP Code:</label>
          <input
            type="text"
            id="zipCode"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="bio">Bio:</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            rows="4"
            placeholder="Tell us about yourself..."
          />
        </div>

        {error && <div className="error">{error}</div>}
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Updating...' : 'Update Profile'}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
